"use client";

import React, { FC, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import SearchModal, {
  Destination,
} from "../../../(client-components)/(Header)/SearchModal";

// Title-case helper — backend often returns slugs in lowercase ("paris",
// "new-york"). Splits on spaces AND hyphens so "new-york" → "New-York"
// and "buenos aires" → "Buenos Aires".
const titleCase = (s?: string | null): string => {
  if (!s) return "";
  return s
    .split(" ")
    .map((w) =>
      w
        .split("-")
        .map((p) =>
          p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : ""
        )
        .join("-")
    )
    .join(" ");
};

/**
 * Hero search bar — matches the 2026 redesign artboard. Clicking the
 * destination input area OR the CTA opens the SearchModal. Selecting a
 * destination in the modal sets local state (does NOT navigate); the user
 * then has to click "Trouver mon guide" to actually go there.
 *
 * Why this two-step flow: it lets the user verify their pick (which is
 * shown live in the bar) before committing to a navigation, and matches
 * the design's CTA-driven intent.
 */
const HeroSearchBar: FC<{ className?: string }> = ({ className = "" }) => {
  const { t } = useTranslation("guydeez");
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [destination, setDestination] = useState<Destination | null>(null);

  const openModal = () => setOpen(true);

  const handleSubmit = () => {
    if (!destination) {
      // No pick yet — opening the modal is the right next action.
      setOpen(true);
      return;
    }
    const path =
      destination.type === "city"
        ? `/city/${destination.slug}`
        : `/country/${destination.slug}`;
    router.push(path as any);
  };

  const placeholder = t("homepage_hero_search_placeholder", {
    defaultValue: "Où partez-vous ? Ville, pays…",
  });

  return (
    <>
      <div
        className={className}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "stretch",
          gap: 8,
          padding: 8,
          background: "rgba(255,255,255,.96)",
          backdropFilter: "blur(12px)",
          borderRadius: 22,
          boxShadow:
            "0 30px 80px -20px rgba(0,0,0,.5), 0 0 0 1px rgba(255,255,255,.5)",
          maxWidth: 720,
          margin: "0 auto 18px",
          textAlign: "left",
          color: "var(--g-ink-900)",
          position: "relative",
          zIndex: 100,
        }}
      >
        {/* Left zone — clickable input proxy */}
        <button
          type="button"
          onClick={openModal}
          style={{
            display: "block",
            textAlign: "left",
            padding: "12px 22px",
            background: "transparent",
            border: 0,
            borderRadius: 14,
            cursor: "text",
            color: "inherit",
            font: "inherit",
          }}
          aria-label={t("homepage_hero_search_open", {
            defaultValue: "Ouvrir la recherche de destination",
          })}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--g-ink-500)",
              marginBottom: 2,
            }}
          >
            {t("homepage_hero_search_label", { defaultValue: "Destination" })}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: destination ? "var(--g-burgundy)" : "var(--g-ink-500)",
            }}
          >
            <PinIcon />
            <span
              style={{
                fontWeight: destination ? 700 : 500,
                fontSize: 17,
              }}
            >
              {destination
                ? destination.type === "city" && destination.country
                  ? `${titleCase(destination.name)}, ${titleCase(destination.country)}`
                  : titleCase(destination.name)
                : placeholder}
            </span>
          </div>
        </button>

        {/* CTA — submits the chosen destination. If nothing is picked yet
            we open the modal as a friendlier fallback. */}
        <button
          type="button"
          onClick={handleSubmit}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "0 28px",
            background: "var(--g-green)",
            color: "white",
            border: 0,
            borderRadius: 14,
            fontSize: 15,
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 6px 18px rgba(79,191,110,.35)",
            whiteSpace: "nowrap",
          }}
        >
          <SearchIcon />
          {t("homepage_hero_search_cta", { defaultValue: "Trouver mon guide" })}
        </button>
      </div>

      <SearchModal
        open={open}
        onClose={() => setOpen(false)}
        onSelect={(dest) => setDestination(dest)}
      />
    </>
  );
};

const PinIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--g-coral)"
    strokeWidth="2"
  >
    <path d="M12 22s-7-7-7-12a7 7 0 1114 0c0 5-7 12-7 12z" />
    <circle cx="12" cy="10" r="3" fill="var(--g-coral)" stroke="none" />
  </svg>
);

const SearchIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
);

export default HeroSearchBar;
